import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Search, 
  SlidersHorizontal, 
  X, 
  Fuel, 
  Gauge, 
  Calendar,
  Car as CarIcon,
  ChevronDown,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Helper function to convert Google Drive share URL to direct image URL
const getDirectImageUrl = (url) => {
  if (!url) return null;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  if (url.includes('drive.google.com/uc')) return url;
  return url;
};

// Premium Car Card Component
const CarCard = ({ car }) => {
  const isSold = car.status === 'sold';
  const mainImage = car.images?.[0] || '';
  const isValidUrl = mainImage && (mainImage.startsWith('http://') || mainImage.startsWith('https://'));
  const placeholderImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&auto=format&fit=crop';
  const imageUrl = isValidUrl ? getDirectImageUrl(mainImage) : placeholderImage;
  
  return (
    <Link to={`/cars/${car.id}`} data-testid={`car-card-link-${car.sheet_id}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="group relative overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-[#CCAD60]/30 transition-all duration-500"
        data-testid={`car-card-${car.sheet_id}`}
      >
        {/* Image Container - 16:9 aspect ratio for iPhone landscape photos */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={imageUrl}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.target.src = placeholderImage; }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
          
          {/* SOLD Badge */}
          {isSold && (
            <div 
              className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
              data-testid={`sold-badge-${car.sheet_id}`}
            >
              Sold
            </div>
          )}
          
          {/* Featured Badge */}
          {car.featured && !isSold && (
            <div className="absolute top-4 left-4 bg-[#CCAD60] text-[#000000] px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
              Featured
            </div>
          )}
          
          {/* Price Badge */}
          <div className="absolute bottom-4 left-4">
            <span className="font-serif text-2xl text-white font-medium">
              €{car.price.toLocaleString()}
            </span>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <h3 className="font-serif text-lg text-white mb-1 group-hover:text-[#CCAD60] transition-colors">
            {car.make} {car.model}
          </h3>
          {car.trim && (
            <p className="text-xs text-[#CCAD60] uppercase tracking-wider mb-3">
              {car.trim}
            </p>
          )}
          
          {/* Specs Row */}
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-zinc-500 text-sm">
            <span>{car.year}</span>
            <span className="text-zinc-700">•</span>
            <span>{car.mileage.toLocaleString()} mi</span>
            <span className="text-zinc-700">•</span>
            <span>{car.fuel_type}</span>
            <span className="text-zinc-700">•</span>
            <span>{car.transmission}</span>
          </div>
        </div>
        
        {/* Hover Border */}
        <div className="absolute inset-0 border border-[#CCAD60]/0 group-hover:border-[#CCAD60]/30 transition-colors duration-500 pointer-events-none" />
      </motion.div>
    </Link>
  );
};

// Premium Filters Component
const Filters = ({ filters, filterOptions, onFilterChange, onClearFilters }) => {
  const hasActiveFilters = Object.values(filters).some(v => v !== '' && v !== null);
  
  return (
    <div className="bg-[#0A0A0A] border border-white/5 p-6 lg:p-8" data-testid="filters-panel">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <SlidersHorizontal size={18} className="text-[#CCAD60]" />
          <h3 className="label-gold">Filters</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-xs text-zinc-400 hover:text-[#CCAD60] transition-colors uppercase tracking-wider flex items-center gap-1"
            data-testid="clear-filters-btn"
          >
            <X size={14} />
            Clear All
          </button>
        )}
      </div>
      
      <div className="space-y-6">
        {/* Make Filter */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Make</label>
          <Select
            value={filters.make || 'all'}
            onValueChange={(value) => onFilterChange('make', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#000000] border-white/10 text-white h-12 rounded-sm focus:border-[#CCAD60]"
              data-testid="filter-make"
            >
              <SelectValue placeholder="All Makes" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Makes</SelectItem>
              {filterOptions.makes?.map(make => (
                <SelectItem key={make} value={make} className="text-zinc-300">{make}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Price Range */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Price Range</label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="Min €"
              value={filters.min_price || ''}
              onChange={(e) => onFilterChange('min_price', e.target.value)}
              className="input-luxury h-12"
              data-testid="filter-min-price"
            />
            <Input
              type="number"
              placeholder="Max €"
              value={filters.max_price || ''}
              onChange={(e) => onFilterChange('max_price', e.target.value)}
              className="input-luxury h-12"
              data-testid="filter-max-price"
            />
          </div>
        </div>
        
        {/* Year Range */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Year</label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="From"
              value={filters.year_from || ''}
              onChange={(e) => onFilterChange('year_from', e.target.value)}
              className="input-luxury h-12"
              data-testid="filter-year-from"
            />
            <Input
              type="number"
              placeholder="To"
              value={filters.year_to || ''}
              onChange={(e) => onFilterChange('year_to', e.target.value)}
              className="input-luxury h-12"
              data-testid="filter-year-to"
            />
          </div>
        </div>
        
        {/* Fuel Type */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Fuel Type</label>
          <Select
            value={filters.fuel_type || 'all'}
            onValueChange={(value) => onFilterChange('fuel_type', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#000000] border-white/10 text-white h-12 rounded-sm focus:border-[#CCAD60]"
              data-testid="filter-fuel-type"
            >
              <SelectValue placeholder="All Fuel Types" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Fuel Types</SelectItem>
              {filterOptions.fuel_types?.map(type => (
                <SelectItem key={type} value={type} className="text-zinc-300">{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Transmission */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Transmission</label>
          <Select
            value={filters.transmission || 'all'}
            onValueChange={(value) => onFilterChange('transmission', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#000000] border-white/10 text-white h-12 rounded-sm focus:border-[#CCAD60]"
              data-testid="filter-transmission"
            >
              <SelectValue placeholder="All Transmissions" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Transmissions</SelectItem>
              {filterOptions.transmissions?.map(trans => (
                <SelectItem key={trans} value={trans} className="text-zinc-300">{trans}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

// Mobile Filters Toggle
const MobileFiltersToggle = ({ isOpen, onClick, activeCount }) => (
  <button
    onClick={onClick}
    className="lg:hidden flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-5 py-3 text-white"
    data-testid="mobile-filters-toggle"
  >
    <SlidersHorizontal size={18} className="text-[#CCAD60]" />
    <span className="text-sm font-medium">Filters</span>
    {activeCount > 0 && (
      <span className="bg-[#CCAD60] text-[#000000] text-xs px-2 py-0.5 font-bold">{activeCount}</span>
    )}
    <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
  </button>
);

// Main Cars Page Component
const CarsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q') || '';
  
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    make: '', min_price: '', max_price: '', year_from: '', year_to: '', fuel_type: '', transmission: ''
  });
  
  const debouncedFilters = useDebounce(filters, 500);
  
  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${API}/cars/filters`);
        const data = await response.json();
        setFilterOptions(data);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);
  
  // Build URL params helper
  const buildParams = useCallback((pageNum) => {
    const params = new URLSearchParams();
    params.append('page', pageNum.toString());
    params.append('page_size', PAGE_SIZE.toString());
    if (searchQuery) params.append('q', searchQuery);
    if (debouncedFilters.make) params.append('make', debouncedFilters.make);
    if (debouncedFilters.min_price) params.append('min_price', debouncedFilters.min_price);
    if (debouncedFilters.max_price) params.append('max_price', debouncedFilters.max_price);
    if (debouncedFilters.year_from) params.append('year_from', debouncedFilters.year_from);
    if (debouncedFilters.year_to) params.append('year_to', debouncedFilters.year_to);
    if (debouncedFilters.fuel_type) params.append('fuel_type', debouncedFilters.fuel_type);
    if (debouncedFilters.transmission) params.append('transmission', debouncedFilters.transmission);
    return params;
  }, [searchQuery, debouncedFilters]);
  
  // Fetch cars
  useEffect(() => {
    const fetchCars = async () => {
      setLoading(true);
      setPage(1);
      try {
        const params = buildParams(1);
        const response = await fetch(`${API}/cars?${params.toString()}`);
        const data = await response.json();
        setCars(data.cars || []);
        setTotal(data.total || 0);
        setHasMore(data.has_more || false);
      } catch (error) {
        console.error('Failed to fetch cars:', error);
        setCars([]); setTotal(0); setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, [debouncedFilters, searchQuery, buildParams]);
  
  // Load more
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const params = buildParams(nextPage);
      const response = await fetch(`${API}/cars?${params.toString()}`);
      const data = await response.json();
      setCars(prev => [...prev, ...(data.cars || [])]);
      setPage(nextPage);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Failed to load more cars:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, buildParams]);
  
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);
  
  const handleClearFilters = useCallback(() => {
    setFilters({ make: '', min_price: '', max_price: '', year_from: '', year_to: '', fuel_type: '', transmission: '' });
  }, []);
  
  const handleClearSearch = useCallback(() => {
    navigate('/cars');
  }, [navigate]);
  
  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== null).length;
  
  return (
    <div className="min-h-screen bg-[#000000] pt-20 lg:pt-24" data-testid="cars-page">
      {/* Hero Header */}
      <div className="bg-[#0A0A0A] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="label-gold mb-4 block">
              {searchQuery ? 'Search Results' : 'Our Collection'}
            </span>
            <h1 className="font-serif text-3xl lg:text-5xl text-white tracking-tight mb-4">
              {searchQuery ? `Results for "${searchQuery}"` : 'Available Vehicles'}
            </h1>
            <p className="text-zinc-400 max-w-xl">
              {searchQuery 
                ? 'Showing results for your search. Use filters to refine further.'
                : 'Browse our carefully curated selection of premium pre-owned vehicles.'
              }
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Search Clear Banner */}
        {searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-4"
            data-testid="search-results-label"
          >
            <div className="flex items-center gap-3 bg-[#0A0A0A] border border-[#CCAD60]/20 px-4 py-3">
              <Search size={16} className="text-[#CCAD60]" />
              <span className="text-sm text-white">
                Searching: <span className="text-[#CCAD60]">{searchQuery}</span>
              </span>
              <button
                onClick={handleClearSearch}
                className="ml-2 text-zinc-500 hover:text-red-400 transition-colors"
                data-testid="clear-search-btn"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Mobile Filter Toggle + Count */}
        <div className="flex items-center justify-between mb-6 lg:hidden">
          <MobileFiltersToggle 
            isOpen={mobileFiltersOpen}
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            activeCount={activeFilterCount}
          />
          <p className="text-sm text-zinc-500">
            {total} {total === 1 ? 'vehicle' : 'vehicles'}
          </p>
        </div>
        
        {/* Mobile Filters */}
        <AnimatePresence>
          {mobileFiltersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden mb-6"
            >
              <Filters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Desktop Layout */}
        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <div className="hidden lg:block w-72 shrink-0 sticky top-28 self-start">
            <Filters
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </div>
          
          {/* Results */}
          <div className="flex-1">
            {/* Results Count (Desktop) */}
            <div className="hidden lg:flex items-center justify-between mb-8">
              <p className="text-zinc-400">
                Showing <span className="text-white font-medium">{cars.length}</span> of{' '}
                <span className="text-white font-medium">{total}</span> vehicles
              </p>
            </div>
            
            {/* Loading State */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="aspect-video bg-[#0A0A0A] animate-pulse" />
                ))}
              </div>
            ) : cars.length === 0 ? (
              /* Empty State - Professional dealership message */
              <div className="text-center py-20 max-w-lg mx-auto" data-testid="no-results">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#CCAD60]/10 flex items-center justify-center">
                  <CarIcon size={40} className="text-[#CCAD60]" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4">
                  Vehicles Coming Soon
                </h3>
                <p className="text-zinc-400 mb-8 leading-relaxed">
                  Our latest stock will be added shortly. Please contact us for current availability or check back soon.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-[#CCAD60] text-black font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-white transition-colors"
                    data-testid="contact-cta"
                  >
                    Contact Us
                  </Link>
                  <a
                    href="tel:+353868739413"
                    className="inline-flex items-center justify-center gap-2 border border-[#CCAD60] text-[#CCAD60] font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#CCAD60] hover:text-black transition-colors"
                    data-testid="call-cta"
                  >
                    Call 086 873 9413
                  </a>
                </div>
              </div>
            ) : (
              /* Car Grid */
              <>
                <div 
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                  data-testid="car-grid"
                >
                  {cars.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>
                
                {/* Load More */}
                {hasMore && (
                  <div className="mt-12 text-center">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="btn-outline inline-flex items-center gap-2"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More Vehicles
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarsPage;
